import type { Bounds, Vector2 } from '../types';

// Check if two rectangles overlap (AABB collision)
export function checkRectCollision(a: Bounds, b: Bounds): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

// Check if two circles overlap
export function checkCircleCollision(
  aPos: Vector2,
  aRadius: number,
  bPos: Vector2,
  bRadius: number
): boolean {
  const dx = aPos.x - bPos.x;
  const dy = aPos.y - bPos.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  return distance < aRadius + bRadius;
}

// Check if a point is inside a rectangle
export function pointInRect(point: Vector2, rect: Bounds): boolean {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.height
  );
}

// Check if a point is inside a circle
export function pointInCircle(point: Vector2, center: Vector2, radius: number): boolean {
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return dx * dx + dy * dy <= radius * radius;
}

// Get the center of a bounds object
export function getBoundsCenter(bounds: Bounds): Vector2 {
  return {
    x: bounds.x + bounds.width / 2,
    y: bounds.y + bounds.height / 2,
  };
}

// Calculate distance between two points
export function distance(a: Vector2, b: Vector2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

// Normalize a vector
export function normalize(v: Vector2): Vector2 {
  const len = Math.sqrt(v.x * v.x + v.y * v.y);
  if (len === 0) return { x: 0, y: 0 };
  return { x: v.x / len, y: v.y / len };
}

// Linear interpolation
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// Clamp a value between min and max
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
